import { useExecuteCommandMutation, useLazyExecuteCommandWithCacheQuery } from '@redux/api'

import { getCommandRequestData } from '@helpers'
import { ACTION_CODE } from '@constants'
import { ActionResultType, ActionRow, GETVariableDTO, ObjectDataRecord } from '@types'

import { generateCommandCacheKey } from '../../helpers/generateCommandCacheKey'
import { isUseCommandDataFromCache } from '../../helpers/isUseCommandDataFromCache'

export const useSetVariableValue = () => {
  const [executeCommand] = useExecuteCommandMutation()
  const [executeCommandWithCache] = useLazyExecuteCommandWithCacheQuery()

  const setVariableValue = async (data: {
    action: ActionRow
    srcObj?: ObjectDataRecord
    isUseCacheCommandRequest?: boolean
  }) => {
    const { action, srcObj, isUseCacheCommandRequest } = data

    if (
      action?.actionCode === ACTION_CODE.SET_VARIABLE_VALUE &&
      action.jsValue &&
      action.variable
    ) {
      if (action.command) {
        const command = action.command
        const parameters = command?.parameters
          ? getCommandRequestData(command.parameters, srcObj)
          : undefined

        const commandProps = {
          params: parameters,
          objectCode: command?.objectCode as string,
          commandCode: command?.name as string,
          body:
            command?.body && command?.httpMethod !== 'GET'
              ? getCommandRequestData(command.body, srcObj)
              : undefined,
          method: command?.httpMethod as string,
        }

        let isUseCommandCache = false

        if (isUseCacheCommandRequest) {
          isUseCommandCache = isUseCommandDataFromCache(generateCommandCacheKey(commandProps))
        }

        const commandData = isUseCacheCommandRequest
          ? await executeCommandWithCache(commandProps, isUseCommandCache)
          : await executeCommand(commandProps)

        const variableValue = new Function('srcObj', action.jsValue)({ commandData, ...srcObj })

        return {
          actionCode: ACTION_CODE.SET_VARIABLE_VALUE,
          value: {
            [action.variable?.internalId]: { ...action.variable, defaultValue: variableValue },
          },
        } as ActionResultType<Record<string, GETVariableDTO>>
      }

      const variableValue = new Function('srcObj', action.jsValue)(srcObj)

      return {
        actionCode: ACTION_CODE.SET_VARIABLE_VALUE,
        value: {
          [action.variable?.internalId]: {
            ...action.variable,
            defaultValue: variableValue,
          },
        },
      } as ActionResultType<Record<string, GETVariableDTO>>
    }

    return Promise.resolve({
      actionCode: ACTION_CODE.SET_VARIABLE_VALUE,
      value: {},
    } as ActionResultType<Record<string, GETVariableDTO>>)
  }

  return {
    setVariableValue,
  }
}
